"use client";
import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { twMerge } from "tailwind-merge";

interface SectionHeaderProps {
    title: string;
    subtitle?: string;
    className?: string;
    align?: "left" | "center" | "right";
}

export function SectionHeader({ title, subtitle, className, align = "left" }: SectionHeaderProps) {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className={twMerge(
                "mb-12 md:mb-16",
                align === "center" && "text-center",
                align === "right" && "text-right",
                className
            )}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
                className={twMerge(
                    "flex items-center gap-4 mb-4 flex-wrap",
                    align === "center" && "justify-center",
                    align === "right" && "justify-end"
                )}
            >
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={isInView ? { scale: 1, opacity: 1 } : {}}
                    transition={{ duration: 0.4, type: "spring", stiffness: 300, delay: 0.2 }}
                    className="h-2.5 w-2.5 bg-primary shadow-[0_0_18px_rgba(232,89,12,0.5)] rounded-full"
                />
                <motion.h2
                    initial={{ opacity: 0, x: -12 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
                    className="text-3xl md:text-4xl font-black text-white tracking-tight"
                >
                    {title}
                </motion.h2>
                {align === "left" && (
                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={isInView ? { scaleX: 1 } : {}}
                        transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                        className="h-px bg-gradient-to-r from-primary/60 via-primary-light/30 to-transparent flex-1 max-w-[160px]"
                    />
                )}
            </motion.div>
            {subtitle && (
                <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
                    className="text-text-muted max-w-2xl text-lg leading-relaxed"
                >
                    {subtitle}
                </motion.p>
            )}
        </motion.div>
    );
}